import React from "react";
import PageHero from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import { CatalogPackages } from "@/components/CatalogPackages";
import { useSeo, JsonLd } from "@/lib/seo";
import { Camera, Video, Users, Handshake, Info } from "lucide-react";

export default function PhotoVideo() {
  useSeo({
    title: "Photographe & Vidéaste événementiel Hauts-de-France | My Animation",
    description:
      "Forfaits photo, vidéo et pack photo+vidéo pour mariage, entreprise, gala, mairie, guinguette et événements familiaux. Prestations assurées via nos partenaires professionnels sous-traitants.",
    path: "/photo-video",
    keywords: "photographe mariage Nord, vidéaste événementiel Hauts-de-France, pack photo vidéo mariage, photographe entreprise Lille, vidéaste gala",
  });

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Photo & Vidéo événementiel",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    description:
      "Reportage photo et captation vidéo pour mariage, anniversaire, mairie, entreprise, gala, festival. Prestations assurées via des professionnels partenaires sous-traitants.",
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="Photo & Vidéo"
        title={<>Un souvenir <span className="italic gold-text">à la hauteur</span><br />de votre événement.</>}
        subtitle="Forfaits photographe, vidéaste et packs photo + vidéo pour mariage, anniversaire, entreprise, gala, mairie, festival ou événement familial. Prestations assurées par nos partenaires professionnels sous-traitants sélectionnés par My Animation."
        img="/assets/entreprises/entreprise-06.jpg"
      />

      {/* Bloc "réalisé en sous-traitance" — mention légale importante */}
      <section className="py-14 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="card-luxe p-6 md:p-8 border-[#DCB457]/40 flex flex-col md:flex-row gap-6 items-start reveal" data-testid="photo-video-partners-note">
          <Handshake size={28} strokeWidth={1.3} className="text-[#DCB457] shrink-0 mt-1" />
          <div>
            <p className="eyebrow">Nos partenaires photo & vidéo</p>
            <h2 className="font-serif-luxe text-2xl md:text-3xl mt-2">
              Prestations réalisées par des <span className="italic gold-text">professionnels sous-traitants.</span>
            </h2>
            <p className="text-white/70 text-sm md:text-base leading-relaxed mt-4 max-w-3xl">
              Pour vous offrir un rendu réellement premium, les prestations photo et vidéo sont assurées via
              un ou plusieurs prestataires partenaires professionnels, sélectionnés et coordonnés par My
              Animation. Vous conservez un seul interlocuteur, un seul devis, une seule facturation.
              Chaque partenaire est équipé de matériel professionnel, dispose de ses propres assurances RC pro
              et intervient à nos côtés dans le Nord, le Pas-de-Calais, les Hauts-de-France et la Belgique.
            </p>
          </div>
        </div>
      </section>

      {/* 3 sections de forfaits */}
      <CatalogPackages
        category="photo-video"
        title={<>Nos forfaits <span className="italic gold-text">photo & vidéo.</span></>}
        subtitle="Photo seule, vidéo seule ou pack combiné — trois niveaux (Essentiel, Événement, Prestige) + pack mariage journée complète."
        testidPrefix="photovideo-pack"
        footerNote="Tarifs TTC · TVA non applicable art. 293 B du CGI · Prestations réalisées via nos partenaires sous-traitants · Devis personnalisé selon durée, lieu, nombre d'invités et options"
      />

      {/* Trois cards catégorielles pour l'harmonie visuelle */}
      <section className="py-20 md:py-24 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1200px] mx-auto">
          <div className="mb-12 reveal">
            <span className="eyebrow">Comment choisir</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Photo, vidéo <span className="italic gold-text">ou les deux&nbsp;?</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Camera, t: "Photo seule", d: "Idéal pour immortaliser les moments clés — cérémonie, cocktail, invités, groupes. Livraison en galerie privée en ligne (50 à 500 photos selon forfait)." },
              { icon: Video, t: "Vidéo seule", d: "Le rendu émotion — captation dynamique + montage. Film souvenir 1 à 6 minutes selon forfait, avec version courte pour les réseaux sociaux." },
              { icon: Users, t: "Pack Photo + Vidéo", d: "Le meilleur des deux mondes — un photographe ET un vidéaste couvrent votre événement. Notre formule la plus choisie pour les mariages et grandes soirées." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="card-luxe p-8 reveal">
                <Icon size={28} strokeWidth={1} className="text-[#DCB457] mb-4" />
                <h3 className="font-serif-luxe text-2xl">{t}</h3>
                <p className="text-white/60 mt-3 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mentions importantes */}
      <section className="py-14 px-6 md:px-10 lg:px-14 max-w-[1200px] mx-auto">
        <div className="card-luxe p-6 md:p-8 flex items-start gap-4" data-testid="photo-video-legal">
          <Info size={18} className="text-[#DCB457] shrink-0 mt-1" />
          <div className="text-sm text-white/65 leading-relaxed">
            <p className="mb-3">
              <b className="text-white">Informations importantes.</b> Les forfaits photo et vidéo sont proposés à titre indicatif.
              Le tarif définitif est établi sur devis selon : la date, la durée de présence, le lieu, le nombre d'invités,
              le type d'événement, les besoins en montage, le nombre de photos souhaitées, le type de film et les options demandées.
            </p>
            <p>
              Les délais de livraison peuvent varier selon la période, la durée de l'événement, le volume de photos et
              le temps de montage nécessaire. Tarifs TTC · TVA non applicable, article 293 B du CGI.
            </p>
          </div>
        </div>
      </section>

      <CTAFooter />
    </>
  );
}
